import type { Note, NoteFolder, NoteTemplate } from '../types/notes'

export const OWNER_ID = 'CAMP-4471X92B'
export const OWNER_NAME = 'Saviour Okon'
export const OWNER_HANDLE = 'saviour'

/**
 * Seeded folder tree. This is an example the user can dismantle entirely —
 * nothing in the module depends on these existing.
 */
export const seedFolders: NoteFolder[] = [
  { id: 'f-school', name: 'School', parentId: null },
  { id: 'f-sye', name: 'Systems Engineering', parentId: 'f-school' },
  { id: 'f-or', name: 'Operations Research', parentId: 'f-sye' },
  { id: 'f-control', name: 'Control Systems', parentId: 'f-sye' },
  { id: 'f-gst', name: 'General Studies', parentId: 'f-school' },
  { id: 'f-personal', name: 'Personal', parentId: null },
  { id: 'f-projects', name: 'Projects', parentId: null },
  { id: 'f-solar', name: 'Solar Dryer', parentId: 'f-projects' },
]

function block(
  kind: Note['blocks'][number]['kind'],
  html: string,
  extra: Partial<Note['blocks'][number]> = {},
): Note['blocks'][number] {
  return { id: `b-${Math.random().toString(36).slice(2, 9)}`, kind, html, ...extra }
}

const base = {
  ownerId: OWNER_ID,
  ownerName: OWNER_NAME,
  contentFormat: 'campuzon-blocks-v1' as const,
  institution: 'University of Lagos',
  academicSession: '2026/2027',
  semester: 'First semester',
  devices: ['Web · Chrome', 'Android · Campuzon 2.4'],
}

export const seedNotes: Note[] = [
  {
    ...base,
    id: 'n-1',
    title: 'Thermodynamics revision',
    blocks: [
      block('heading', 'Key concepts', { level: 2 }),
      block(
        'paragraph',
        'The <strong>first law</strong> is a bookkeeping statement: energy in a closed system is conserved, so any change in internal energy equals heat added minus work done.',
      ),
      block('bulleted', 'Isothermal — temperature held constant, internal energy unchanged'),
      block('bulleted', 'Adiabatic — no heat exchange, so all work comes from internal energy'),
      block('bulleted', 'Isobaric — pressure constant, work is <em>p·ΔV</em>'),
      block('heading', 'Questions to bring to tutorial', { level: 2 }),
      block('checklist', 'Why is entropy defined by a reversible path?', { checked: true }),
      block('checklist', 'Sign convention for work in the Nigerian syllabus', { checked: false }),
      block('quote', 'Entropy is time’s arrow written in mathematics.'),
      block('separator', ''),
      block('paragraph', 'Bernoulli comparison lives in the fluid mechanics note.'),
    ],
    folderId: 'f-sye',
    tags: ['exam', 'revision'],
    attachments: [
      {
        id: 'a-1',
        kind: 'image',
        name: 'whiteboard-cycle-diagram.png',
        size: '1.4 MB',
        addedOn: '9 Aug 2026',
        previewUrl:
          'https://images.unsplash.com/photo-1509228468518-180dd4864904?auto=format&fit=crop&w=640&q=60',
      },
      {
        id: 'a-2',
        kind: 'document',
        name: 'carnot-cycle-handout.pdf',
        size: '820 KB',
        addedOn: '9 Aug 2026',
      },
    ],
    academicLinks: [
      { kind: 'Course', label: 'SYE 311 — Operations Research' },
      { kind: 'Semester', label: 'First semester, 2026/2027' },
    ],
    createdAt: '2 Aug 2026',
    updatedAt: '11 Aug 2026, 21:14',
    lastOpenedAt: '2 hours ago',
    isPrivate: true,
    locked: false,
    shares: [],
    versions: [
      {
        id: 'v-3',
        savedOn: '11 Aug 2026, 21:14',
        author: 'You',
        summary: 'Added tutorial questions',
        title: 'Thermodynamics revision',
        blocks: [
          block('heading', 'Second law', { level: 2 }),
          block(
            'paragraph',
            'Entropy of an isolated system never decreases. Every real process increases total entropy.',
          ),
          block('bulleted', 'Reversible processes are the limiting ideal case.'),
        ].map((item, index) => ({ ...item, id: `v3-b${index}` })),
      },
      {
        id: 'v-2',
        savedOn: '9 Aug 2026, 18:02',
        author: 'You',
        summary: 'Attached whiteboard photo',
        title: 'Thermodynamics revision',
        blocks: [
          block('heading', 'Second law', { level: 2 }),
          block('paragraph', 'Entropy of an isolated system never decreases.'),
        ].map((item, index) => ({ ...item, id: `v2-b${index}` })),
      },
      { id: 'v-1', savedOn: '2 Aug 2026, 09:40', author: 'You', summary: 'Created from Study template' },
    ],
    syncState: 'synced',
    lifecycle: 'active',
    pinned: true,
  },
  {
    ...base,
    id: 'n-2',
    title: 'Final year project research',
    blocks: [
      block('heading', 'Objective', { level: 2 }),
      block(
        'paragraph',
        'Design and fabricate a solar dryer sized for smallholder cassava processing, targeting a moisture reduction from 65% to 12% within two days of clear weather.',
      ),
      block('heading', 'Open decisions', { level: 2 }),
      block('table', '', {
        rows: [
          ['Decision', 'Options', 'Leaning'],
          ['Collector type', 'Flat plate / evacuated tube', 'Flat plate — cost'],
          ['Airflow', 'Natural / forced convection', 'Forced, small PV fan'],
          ['Tray material', 'Steel mesh / food-grade plastic', 'Mesh'],
        ],
      }),
      block('checklist', 'Confirm supervisor is happy with forced convection', { checked: true }),
      block('checklist', 'Cost the PV fan and controller', { checked: false }),
      block('checklist', 'Book workshop time for the frame', { checked: false }),
    ],
    folderId: 'f-solar',
    tags: ['project', 'research', 'important'],
    attachments: [
      {
        id: 'a-3',
        kind: 'document',
        name: 'dryer-sizing-calculations.xlsx',
        size: '340 KB',
        addedOn: '7 Aug 2026',
      },
    ],
    academicLinks: [{ kind: 'Project', label: 'Design and Fabrication of Solar Dryer' }],
    createdAt: '14 Jul 2026',
    updatedAt: '12 Aug 2026, 08:30',
    lastOpenedAt: 'yesterday',
    isPrivate: false,
    locked: false,
    shares: [
      {
        id: 's-1',
        displayName: 'Daniel Uzo',
        handle: 'danielu',
        role: 'editor',
        grantedOn: '20 Jul 2026',
      },
      {
        id: 's-2',
        displayName: 'Tolu Adeleke',
        handle: 'tolua',
        role: 'viewer',
        grantedOn: '20 Jul 2026',
      },
    ],
    shareLink: { id: 'sl-1', role: 'viewer', createdOn: '22 Jul 2026', active: false },
    versions: [
      { id: 'v-6', savedOn: '12 Aug 2026, 08:30', author: 'Daniel Uzo', summary: 'Updated airflow row' },
      { id: 'v-5', savedOn: '7 Aug 2026, 16:11', author: 'You', summary: 'Added sizing spreadsheet' },
    ],
    syncState: 'synced',
    lifecycle: 'active',
    pinned: true,
    reminder: { dateLabel: '20 Aug 2026', timeLabel: '08:00', sortKey: '2026-08-20T08:00' },
  },
  {
    ...base,
    id: 'n-3',
    title: 'Fluid mechanics — Bernoulli',
    blocks: [
      block('heading', 'Bernoulli equation', { level: 2 }),
      block(
        'paragraph',
        'Along a streamline in steady, incompressible, inviscid flow the sum of pressure head, velocity head and elevation head is constant.',
      ),
      block('numbered', 'Identify two points on the same streamline'),
      block('numbered', 'Write the heads at each point'),
      block('numbered', 'Solve for the unknown, checking units throughout'),
      block(
        'paragraph',
        'Careful: the inviscid assumption breaks down in long pipe runs, where a friction loss term is needed.',
      ),
    ],
    folderId: 'f-sye',
    tags: ['exam'],
    attachments: [],
    academicLinks: [{ kind: 'Class', label: 'SYE 305 — Control Systems tutorial' }],
    createdAt: '28 Jul 2026',
    updatedAt: '5 Aug 2026, 14:20',
    lastOpenedAt: '4 days ago',
    isPrivate: true,
    locked: false,
    shares: [],
    versions: [],
    syncState: 'synced',
    lifecycle: 'active',
    pinned: false,
  },
  {
    ...base,
    id: 'n-4',
    title: 'Housing shortlist — Yaba side',
    blocks: [
      block('paragraph', 'Three places worth a physical visit before committing a deposit.'),
      block('checklist', 'Cedarwood Residence — verified, ₦520k, 12 min walk', { checked: true }),
      block('checklist', 'Ravine Court — deposit feels high, ask about water', { checked: false }),
      block('checklist', 'Ask Ifeoma whether she still wants to share', { checked: false }),
      block('quote', 'Never pay a deposit before the inspection. Campuzon says the same thing.'),
    ],
    folderId: 'f-personal',
    tags: ['important'],
    attachments: [],
    academicLinks: [],
    createdAt: '3 Aug 2026',
    updatedAt: '10 Aug 2026, 19:05',
    lastOpenedAt: '2 days ago',
    isPrivate: true,
    locked: false,
    shares: [],
    versions: [],
    syncState: 'offline',
    lifecycle: 'active',
    pinned: false,
  },
  {
    ...base,
    id: 'n-5',
    title: '14 August 2026',
    blocks: [
      block(
        'paragraph',
        'Started properly on the project today. The workshop was quieter than usual so I got the frame measurements down without anyone needing the bench.',
      ),
      block(
        'paragraph',
        'Still uneasy about the fan budget. If the PV panel comes in above ₦40k I will have to argue for natural convection instead, which weakens the whole premise.',
      ),
      block('heading', 'Things to remember', { level: 3 }),
      block('checklist', 'Call home on Sunday', { checked: false }),
      block('checklist', 'Return the caliper to the lab', { checked: true }),
    ],
    folderId: null,
    tags: ['personal'],
    attachments: [
      {
        id: 'a-4',
        kind: 'audio',
        name: 'thought-on-the-bus.m4a',
        size: '1.1 MB',
        addedOn: '14 Aug 2026',
        durationSeconds: 96,
        transcriptionState: 'done',
        transcript:
          'Remember to ask the project supervisor whether the solar dryer airflow calculations need the revised humidity figures before Friday.',
      },
    ],
    academicLinks: [],
    createdAt: '14 Aug 2026',
    updatedAt: '14 Aug 2026, 22:41',
    lastOpenedAt: 'today',
    isPrivate: true,
    locked: true,
    shares: [],
    versions: [],
    syncState: 'synced',
    lifecycle: 'active',
    pinned: false,
    diaryDate: '14 Aug 2026',
    mood: 'Focused',
  },
  {
    ...base,
    id: 'n-6',
    title: '11 August 2026',
    blocks: [
      block(
        'paragraph',
        'Long day. Two lectures back to back and then the accommodation viewing, which was not what the photographs suggested.',
      ),
      block('paragraph', 'Reported the listing afterwards. It felt worth doing.'),
    ],
    folderId: null,
    tags: ['personal'],
    attachments: [],
    academicLinks: [],
    createdAt: '11 Aug 2026',
    updatedAt: '11 Aug 2026, 23:12',
    lastOpenedAt: '3 days ago',
    isPrivate: true,
    locked: false,
    shares: [],
    versions: [],
    syncState: 'synced',
    lifecycle: 'active',
    pinned: false,
    diaryDate: '11 Aug 2026',
    mood: 'Tired',
  },
  {
    ...base,
    id: 'n-7',
    title: 'Departmental meeting — 6 Aug',
    blocks: [
      block('heading', 'Participants', { level: 3 }),
      block('paragraph', 'Prof. N. Okonkwo, Dr. F. Adeyemi, class representatives'),
      block('heading', 'Decisions', { level: 3 }),
      block('bulleted', 'Mid-semester test moved to week 9'),
      block('bulleted', 'Laboratory groups capped at six'),
      block('heading', 'Action items', { level: 3 }),
      block('checklist', 'Circulate revised test date to the class group', { checked: true }),
      block('checklist', 'Confirm lab group list with the technologist', { checked: false }),
    ],
    folderId: 'f-school',
    tags: ['meeting'],
    attachments: [],
    academicLinks: [{ kind: 'Calendar event', label: 'Departmental meeting, 6 Aug 2026' }],
    createdAt: '6 Aug 2026',
    updatedAt: '6 Aug 2026, 17:50',
    lastOpenedAt: '8 days ago',
    isPrivate: true,
    locked: false,
    shares: [],
    versions: [],
    syncState: 'synced',
    lifecycle: 'archived',
    pinned: false,
  },
  {
    ...base,
    id: 'n-8',
    title: 'Ideas — anything',
    blocks: [
      block('paragraph', 'Captured on the walk back. Unsorted on purpose.'),
      block('bulleted', 'A price-per-minute-to-campus chart would settle the housing argument'),
      block('bulleted', 'Ask the department for last year’s past papers'),
    ],
    folderId: null,
    tags: [],
    attachments: [],
    academicLinks: [],
    createdAt: '13 Aug 2026',
    updatedAt: '13 Aug 2026, 07:58',
    lastOpenedAt: 'yesterday',
    isPrivate: true,
    locked: false,
    shares: [],
    versions: [],
    syncState: 'synced',
    lifecycle: 'active',
    pinned: false,
  },
  {
    ...base,
    id: 'n-9',
    title: 'Old GST notes',
    blocks: [block('paragraph', 'Superseded by the revision note. Kept in case the test format changes.')],
    folderId: 'f-gst',
    tags: [],
    attachments: [],
    academicLinks: [],
    createdAt: '19 Jul 2026',
    updatedAt: '19 Jul 2026, 11:02',
    lastOpenedAt: '3 weeks ago',
    isPrivate: true,
    locked: false,
    shares: [],
    versions: [],
    syncState: 'synced',
    lifecycle: 'trashed',
    pinned: false,
    trashedOn: '10 Aug 2026',
  },
  {
    ...base,
    id: 'n-10',
    ownerId: 'CAMP-8812K04M',
    ownerName: 'Ifeoma Eze',
    title: 'SYE 311 group study plan',
    blocks: [
      block('heading', 'Plan for the next three weeks', { level: 2 }),
      block('paragraph', 'Splitting the syllabus so nobody revises the same topic twice.'),
      block('bulleted', 'Week 1 — Ifeoma: duality and sensitivity'),
      block('bulleted', 'Week 2 — Saviour: queueing and inventory'),
      block('bulleted', 'Week 3 — Daniel: dynamic programming'),
      block('paragraph', 'Everyone brings three past-paper questions to each session.'),
    ],
    folderId: null,
    tags: ['exam'],
    attachments: [],
    academicLinks: [{ kind: 'Course', label: 'SYE 311 — Operations Research' }],
    createdAt: '30 Jul 2026',
    updatedAt: '12 Aug 2026, 12:20',
    lastOpenedAt: 'yesterday',
    isPrivate: false,
    locked: false,
    shares: [
      {
        id: 's-3',
        displayName: OWNER_NAME,
        handle: OWNER_HANDLE,
        role: 'viewer',
        grantedOn: '30 Jul 2026',
      },
    ],
    comments: [
      {
        id: 'c-1',
        authorName: 'Ifeoma Eze',
        authorHandle: 'ifeoma.e',
        body: 'Does week 2 work for you @saviour? Happy to swap if queueing clashes with your project week.',
        postedOn: '12 Aug 2026, 12:20',
        mentions: [{ handle: 'saviour', displayName: OWNER_NAME }],
        resolved: false,
        replies: [],
      },
    ],
    versions: [],
    syncState: 'synced',
    lifecycle: 'active',
    pinned: false,
  },
  {
    ...base,
    id: 'n-11',
    ownerId: 'CAMP-3390T71D',
    ownerName: 'Daniel Uzo',
    title: 'Workshop booking sheet',
    blocks: [
      block('paragraph', 'Slots I have already taken so we do not double-book the bench.'),
      block('table', '', {
        rows: [
          ['Day', 'Time', 'Who'],
          ['Monday', '10:00 — 12:00', 'Daniel'],
          ['Wednesday', '14:00 — 16:00', 'Free'],
        ],
      }),
    ],
    folderId: null,
    tags: ['project'],
    attachments: [],
    academicLinks: [{ kind: 'Project', label: 'Design and Fabrication of Solar Dryer' }],
    createdAt: '5 Aug 2026',
    updatedAt: '13 Aug 2026, 09:45',
    lastOpenedAt: 'today',
    isPrivate: false,
    locked: false,
    shares: [
      {
        id: 's-4',
        displayName: OWNER_NAME,
        handle: OWNER_HANDLE,
        role: 'editor',
        grantedOn: '5 Aug 2026',
      },
    ],
    versions: [],
    syncState: 'syncing',
    lifecycle: 'active',
    pinned: false,
  },
]

export const noteTemplates: NoteTemplate[] = [
  {
    key: 'blank',
    label: 'Blank note',
    description: 'Just start writing.',
    defaultTitle: 'Untitled note',
    blocks: [{ kind: 'paragraph', html: '' }],
  },
  {
    key: 'lecture',
    label: 'Lecture note',
    description: 'Course, lecturer, topic, then notes and key points.',
    defaultTitle: 'Lecture note',
    blocks: [
      { kind: 'paragraph', html: '<strong>Course:</strong> ' },
      { kind: 'paragraph', html: '<strong>Lecturer:</strong> ' },
      { kind: 'paragraph', html: '<strong>Topic:</strong> ' },
      { kind: 'heading', html: 'Notes', level: 2 },
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'Key points', level: 2 },
      { kind: 'bulleted', html: '' },
    ],
  },
  {
    key: 'study',
    label: 'Study note',
    description: 'Concepts, questions and a summary for revision.',
    defaultTitle: 'Study note',
    blocks: [
      { kind: 'paragraph', html: '<strong>Subject:</strong> ' },
      { kind: 'paragraph', html: '<strong>Topic:</strong> ' },
      { kind: 'heading', html: 'Key concepts', level: 2 },
      { kind: 'bulleted', html: '' },
      { kind: 'heading', html: 'Questions', level: 2 },
      { kind: 'checklist', html: '', checked: false },
      { kind: 'heading', html: 'Summary', level: 2 },
      { kind: 'paragraph', html: '' },
    ],
  },
  {
    key: 'assignment',
    label: 'Assignment note',
    description: 'Question, research, answer and references.',
    defaultTitle: 'Assignment note',
    blocks: [
      { kind: 'paragraph', html: '<strong>Course:</strong> ' },
      { kind: 'paragraph', html: '<strong>Assignment:</strong> ' },
      { kind: 'heading', html: 'Question', level: 2 },
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'Research', level: 2 },
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'Answer', level: 2 },
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'References', level: 2 },
      { kind: 'numbered', html: '' },
    ],
  },
  {
    key: 'meeting',
    label: 'Meeting note',
    description: 'Participants, agenda, decisions and action items.',
    defaultTitle: 'Meeting note',
    blocks: [
      { kind: 'paragraph', html: '<strong>Date:</strong> ' },
      { kind: 'paragraph', html: '<strong>Participants:</strong> ' },
      { kind: 'heading', html: 'Agenda', level: 2 },
      { kind: 'numbered', html: '' },
      { kind: 'heading', html: 'Discussion', level: 2 },
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'Decisions', level: 2 },
      { kind: 'bulleted', html: '' },
      { kind: 'heading', html: 'Action items', level: 2 },
      { kind: 'checklist', html: '', checked: false },
    ],
  },
  {
    key: 'project',
    label: 'Project note',
    description: 'Objective, tasks, research, decisions and next steps.',
    defaultTitle: 'Project note',
    blocks: [
      { kind: 'paragraph', html: '<strong>Project:</strong> ' },
      { kind: 'heading', html: 'Objective', level: 2 },
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'Tasks', level: 2 },
      { kind: 'checklist', html: '', checked: false },
      { kind: 'heading', html: 'Research', level: 2 },
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'Decisions', level: 2 },
      { kind: 'bulleted', html: '' },
      { kind: 'heading', html: 'Next steps', level: 2 },
      { kind: 'paragraph', html: '' },
    ],
  },
  {
    key: 'diary',
    label: 'Daily diary',
    description: 'A dated entry with mood and things to remember.',
    defaultTitle: 'Diary entry',
    blocks: [
      { kind: 'paragraph', html: '' },
      { kind: 'heading', html: 'Things to remember', level: 3 },
      { kind: 'checklist', html: '', checked: false },
    ],
  },
]

/** Campuzon users the owner can share with. */
export const shareCandidates = [
  { displayName: 'Ifeoma Eze', handle: 'ifeoma.e' },
  { displayName: 'Daniel Uzo', handle: 'danielu' },
  { displayName: 'Tolu Adeleke', handle: 'tolua' },
  { displayName: 'Amaka Nwosu', handle: 'amakan' },
  { displayName: 'Prof. N. Okonkwo', handle: 'n.okonkwo' },
]
